<template>
  <div class="memory-book-coupon">
    <!-- A valid code: what it takes off, and the way to remove it. -->
    <div class="memory-book-coupon__applied" v-if="coupon && !coupon.error">
      <p role="status">
        <svg aria-hidden="true" viewBox="0 0 448 512">
          <use href="#check" />
        </svg>
        <span v-text="discountLine" />
      </p>
      <button
        class="memory-book-coupon__link"
        type="button"
        @click="remove"
        v-text="texts.couponRemove"
      />
    </div>

    <button
      class="memory-book-coupon__link"
      type="button"
      v-else-if="!shown"
      @click="focusInput"
      v-text="texts.couponAsk"
    />

    <form
      class="memory-book-coupon__form"
      novalidate
      v-else
      @submit.prevent="apply"
    >
      <label class="memory-book-panel__label" :for="inputId">
        {{ texts.couponLabel }}
      </label>
      <div class="memory-book-coupon__row">
        <input
          autocapitalize="characters"
          autocomplete="off"
          class="memory-book-coupon__input"
          maxlength="64"
          spellcheck="false"
          type="text"
          ref="input"
          v-model="draft"
          :aria-describedby="refusal ? refusalId : undefined"
          :aria-invalid="refusal ? 'true' : undefined"
          :id="inputId"
        >
        <button
          class="btn obituary memory-book-coupon__apply"
          type="submit"
          :disabled="checking || !draft.trim()"
        >
          <span
            aria-hidden="true"
            class="memory-book-panel__spinner"
            v-if="checking"
          />
          {{ texts.couponApply }}
        </button>
      </div>
      <p
        class="slide-in-modal__error-message"
        role="alert"
        v-if="refusal"
        :id="refusalId"
        v-text="refusal"
      />
    </form>
  </div>
</template>

<script lang="ts" setup>
  import { sprintf } from "sprintf-js";
  import { computed, nextTick, ref, useId } from "vue";
  import {
    type BookCoupon,
    type CouponErrorCode,
    checkCoupon,
    formatPrice,
  } from "../../highlights/memory-book";
  import { memoryBookTexts } from "../../highlights/texts";
  import { trackBook } from "../../slideshow/track";

  declare const lavendlaObituary: { backendAPIUrl: string };

  /**
   * "Har du en rabattkod?" under the sizes (PRO-4618): a code, "Använd", then what it takes
   * off, or why it was refused in plain words. The API does the maths; the panel shows the
   * prices it answered, and Stripe the exact total.
   */
  const props = defineProps<{ currency: string; orderId: string }>();
  const coupon = defineModel<BookCoupon | null>({ required: true });

  const texts = memoryBookTexts();
  const inputId = useId();
  const refusalId = `${inputId}-refusal`;
  const input = ref<HTMLInputElement | null>(null);
  const shown = ref(false);
  const draft = ref("");
  const checking = ref(false);

  const REFUSALS: Record<CouponErrorCode, string> = {
    EXPIRED_ERROR: texts.couponExpired,
    MAX_USES_ERROR: texts.couponUsedUp,
    MINIMUM_AMOUNT_ERROR: texts.couponTooLow,
    NOT_APPLICABLE_ERROR: texts.couponNotForBook,
    NOT_FOUND_ERROR: texts.couponNotFound,
    rate_limited: texts.errorRateLimited,
    unavailable: texts.couponUnavailable,
    WRONG_CURRENCY_ERROR: texts.couponWrongMarket,
  };

  const refusal = computed(() =>
    coupon.value?.error ? REFUSALS[coupon.value.error] : ""
  );

  const discountLine = computed(() => {
    const current = coupon.value;
    if (!current) {
      return "";
    }
    return current.type === "percent"
      ? sprintf(texts.couponPercent, current.code, current.value)
      : sprintf(
          texts.couponAmount,
          current.code,
          formatPrice(current.value, props.currency)
        );
  });

  async function focusInput() {
    shown.value = true;
    await nextTick();
    input.value?.focus();
  }

  async function apply() {
    const code = draft.value.trim();
    if (!code || checking.value) {
      return;
    }
    checking.value = true;
    const answer = await checkCoupon({
      apiUrl: lavendlaObituary.backendAPIUrl,
      code,
      orderId: props.orderId,
    });
    checking.value = false;
    coupon.value = answer;
    trackBook(
      answer.error
        ? { action: "coupon_refused", reason: answer.error }
        : { action: "coupon_applied" }
    );
    if (answer.error) {
      await focusInput();
    }
  }

  function remove() {
    coupon.value = null;
    draft.value = "";
    focusInput();
  }

  /** The panel's way back here when paying refuses the code: the field, with the reason. */
  function refused(error: CouponErrorCode) {
    const current = coupon.value;
    coupon.value = {
      code: current?.code ?? draft.value,
      error,
      type: null,
      unitPrices: {},
      value: 0,
    };
    draft.value = current?.code ?? draft.value;
    focusInput();
  }

  defineExpose({ refused });
</script>
